// Clinic / client DTOs. A clinic is the external practice that sends cases to the lab; its
// clinic_code becomes a per-clinic case-number segment, and its name is the denormalized snapshot
// stamped onto each case. Imported by both server (routes) and client (portal + admin).
import { z } from 'zod';

export const CLIENT_TYPES = ['dentist', 'clinic', 'hospital'] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];
export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  dentist: 'Dentist',
  clinic: 'Clinic',
  hospital: 'Hospital',
};

/** Whether a clinic has a portal login, and its state. Null = no login provisioned yet. */
export interface ClinicPortalStatus {
  userId: string;
  username: string;
  active: boolean;
}

export interface Clinic {
  id: string;
  clinicCode: string;
  name: string;
  clientType: ClientType;
  dentistName: string | null;
  contactPerson: string | null;
  mobile: string | null;
  whatsapp: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Portal-login status (populated on list/detail reads); null when none exists. */
  portal?: ClinicPortalStatus | null;
}

const optText = (max: number) => z.string().trim().max(max).nullish();

export const createClinicSchema = z.object({
  /**
   * The lab's filing reference. OPTIONAL: leave it out and the server allocates the next code in the
   * CL-000123 series, the same one public signup draws on. Hand-typed codes are what produced a list
   * reading `123`, `005`, `CHK1`, `AITAZAZULHAQ` — no two filed the same way, and since a clinic's
   * doctor group is derived from this code, an improvised one propagates. A code may still be given
   * explicitly for a clinic the lab already files under its own reference.
   */
  clinicCode: z
    .string()
    .trim()
    .min(1)
    .max(24)
    .regex(/^[A-Za-z0-9-]+$/, 'Letters, digits and hyphens only')
    .transform((s) => s.toUpperCase())
    .optional(),
  name: z.string().trim().min(1).max(160),
  clientType: z.enum(CLIENT_TYPES).default('clinic'),
  dentistName: optText(160),
  contactPerson: optText(160),
  mobile: optText(40),
  whatsapp: optText(40),
  email: z.string().trim().email().max(160).nullish(),
  city: optText(80),
  address: optText(300),
  notes: optText(2000),
});
export type CreateClinicRequest = z.infer<typeof createClinicSchema>;

export const updateClinicSchema = z
  .object({
    name: z.string().trim().min(1).max(160).optional(),
    clientType: z.enum(CLIENT_TYPES).optional(),
    dentistName: optText(160),
    contactPerson: optText(160),
    mobile: optText(40),
    whatsapp: optText(40),
    email: z.string().trim().email().max(160).nullish(),
    city: optText(80),
    address: optText(300),
    notes: optText(2000),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateClinicRequest = z.infer<typeof updateClinicSchema>;

export interface ClinicListQuery {
  search?: string;
  clientType?: ClientType;
  active?: boolean;
  limit: number;
  offset: number;
}

/** Provision a clinic-portal login (role 'clinic', users.clinic_id set to the clinic). */
export const clinicPortalAccessSchema = z.object({
  username: z.string().trim().min(3).max(60),
  password: z.string().min(8).max(200),
  displayName: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(160).nullish(),
});
export type ClinicPortalAccessRequest = z.infer<typeof clinicPortalAccessSchema>;

/** Reset the password of an existing clinic-portal login. */
export const clinicPortalResetSchema = z.object({
  password: z.string().min(8).max(200),
});
export type ClinicPortalResetRequest = z.infer<typeof clinicPortalResetSchema>;

/** Enable or disable an existing clinic-portal login (without deleting it). */
export const clinicPortalActiveSchema = z.object({
  active: z.boolean(),
});
export type ClinicPortalActiveRequest = z.infer<typeof clinicPortalActiveSchema>;

/** Result of provisioning / resetting a clinic-portal login. */
export interface ClinicPortalAccessResult {
  userId: string;
  username: string;
  active: boolean;
}

/**
 * Public clinic self-registration (alignova.cloud/register and the alignovaaligners.com /register
 * page). The account is created ACTIVE: a doctor can sign in and submit the moment they register.
 * Bots are kept out by the proof-of-work challenge, the honeypot and the rate limiter.
 *
 * Deliberately short. Two fields that used to be here are gone:
 *
 *   - the clinic code, because it is the lab's filing reference, not something a doctor has any way
 *     of choosing well. Asking for it invited a collision with a code already on the books and a
 *     rejection the person could do nothing about. The server allocates it now, from the same
 *     CL-000123 series a rep's registration draws on, so every clinic is numbered the one way.
 *   - the email, because this lab reaches clinics by phone and WhatsApp. Nothing in the product
 *     sends mail, so an address collected here only ever sat unused.
 */
export const clinicSignupSchema = z.object({
  name: z.string().trim().min(1).max(160),
  dentistName: z.string().trim().min(1).max(160),
  mobile: z.string().trim().min(5).max(40),
  whatsapp: z.string().trim().min(5, 'WhatsApp number is required').max(40),
  city: z.string().trim().min(1, 'City is required').max(80),
  username: z.string().trim().min(3).max(60),
  password: z.string().min(8).max(200),
});
export type ClinicSignupRequest = z.infer<typeof clinicSignupSchema>;
